<style>
.number-circle {
    border: 2px solid var(--z-primary);
    color: var(--z-primary);
    border-radius: 50%;
    width: 20px;
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 10px;
    background-color: white;
    box-sizing: content-box;
    position: relative;
}
.checkmark-circle {
    border: 2px solid var(--z-primary);
    background-color: var(--z-primary);
    color: var(--z-primarytext);
    width: 12px;
    height: 12px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    left: -12px;
    top: 6px;
    font-size: 10px;
}
.checkmark {
    display: inline-block;
    font-size: 12px;
}
.team-image {
    width: 40px;
    height: 40px;
}
</style>
<template>
    <v-container>
        <div class="font-weight-bold mb-2 align-center d-flex">
            <img :src="'/api/public/image/get?size=200x200&type=team&id=' + team.id" class="team-image mr-2" />
            <div>{{team.name}}</div>
        </div>

        <div v-if="team.registration_answers">
            <div v-for="question in questions" :key="question.name">
                <div class="d-flex fill-width">
                    <div class="font-weight-bold">{{ question.name }}</div>
                    <div class="ml-2">{{ team.registration_answers[question.name] }}</div>
                </div>
            </div>
        </div>

        <!-- Team leaders -->
        <div class="font-weight-bold mb-1">{{$translate('Leaders')}}</div>
        <div class="d-flex" v-for="user in leaders" :key="user.id">
            <div class="d-flex fill-width" style="margin: 0.15rem; position: relative; display: inline-flex;">
                <v-avatar :size="20" class="mr-2">
                    <img :src="'/api/memberapi/image/member/get?size=50x50&id=' + user.id" class="user-image" />
                </v-avatar>
                <div class="flex-grow-1 text-truncate">{{ user.name }}</div>

                <a :href="'mailto:' + user.mail" v-if="user.mail && showUserDetails">
                    <template v-if="$vuetify.breakpoint.mobile">
                        <v-icon color="primary">mdi-email-outline</v-icon>
                    </template>
                    <template v-else>
                        {{ user.mail }}
                    </template>
                </a>
                <a :href="'tel:' + user.phone" v-if="user.phone && showUserDetails" :class="{ 'ml-2': user.mail }">
                    <template v-if="$vuetify.breakpoint.mobile">
                        <v-icon color="primary">mdi-phone-outline</v-icon>
                    </template>
                    <template v-else>
                        {{ user.phone }}
                    </template>
                </a>
                <v-icon
                    small
                    style="position: absolute; top: 0; left: 0; transform: translate(-50%, -50%);"
                    color="primary"
                    v-if="user.user_type == 'mainLeader'">
                    mdi-star
                </v-icon>
            </div>
        </div>

        <!-- Team captain -->
        <div v-if="requireCaptain">

            <!-- Title -->
            <div class="font-weight-bold mb-1">
                {{ $translate('Captain') }}
                <v-icon v-if="!captain && canEditCaptain" small color="error">mdi-star</v-icon>
            </div>

            <!-- Captain -->
            <div class="d-flex fill-width" style="margin: 0.15rem; position: relative;">
                <div v-if="captain" class="d-flex">
                    <v-avatar :size="20" class="mr-2">
                        <img :src="'/api/memberapi/image/member/get?size=50x50&id=' + captain.id" class="user-image" />
                    </v-avatar>
                    <div class="flex-grow-1 text-truncate">{{ captain.name }}</div>
                </div>

                <div v-else class="text--disabled ml-2">
                    {{$translate('No captain assigned')}}
                </div>

                <div v-if="isTeamLeader" class="ml-2" @click="enableEditCaptain">
                    <v-icon v-if="captain">mdi-pencil</v-icon>
                    <v-icon color="primary" v-else>mdi-plus-circle</v-icon>
                </div>

                <!-- Edit captain -->
                <template v-if="isTeamLeader">
                    <v-dialog v-model="editCaptain.show" max-width="400px" scrollable content-class="z-full-width-dialog">
                        <v-card>
                            <v-card-title>
                                <div class="d-flex fill-width">
                                    <div class="flex-grow-1">{{ $translate('Select team captain') }}</div>
                                    <v-btn icon @click="editCaptain.show = false" class="float-right"><v-icon>mdi-close</v-icon></v-btn>
                                </div>
                            </v-card-title>
                            <v-card-text>
                                <PlayerFormationWithCaptain :players="players" :team="team" :match="match" :showCaptain="true" :picture="true" />
                            </v-card-text>
                        </v-card>
                    </v-dialog>
                </template>
            </div>
        </div>

        <!-- Team formation -->
        <div class="font-weight-bold mt-2">{{$translate('Formation')}}</div>
        <multi-column-list :items="players" @click="({item: user, $event}) => showPlayerDetails(user, $event)" :clickable="showUserDetails && canManageFormation">
            <template #icon="{item: user}">
                <div class="number-circle ml-2">
                    <div v-if="user.shirt_number">{{ user.shirt_number }}</div>
                    <div class="checkmark-circle" v-if="isPresent(user.id)">
                        <span class="checkmark">✔</span>
                    </div>
                </div>
            </template>
            <template #no-items>
                <div class="text--disabled ml-4">
                    {{$translate('No players added to formation')}}
                </div>
            </template>
        </multi-column-list>
        <div v-if="canManageFormation" class="d-flex z-clickable mt-2" key="add" @click="() => showManageFormationDialog(team)">
            <div class="mr-2">
                <v-icon color="primary">mdi-plus-circle</v-icon>
            </div>
            <div class="mb-2">
                <div>{{ $translate('Manage formation') }}</div>
            </div>
        </div>

        <!-- DIALOGS -->
        <TeamsportInvitePlayersDialog
            v-if="manageFormation.show"
            v-model="manageFormation.show"
            :referee="isReferee"
            :match="match"
            :teamId="team.id"
            :group="team.group"
            :activity="manageFormation.activity" />

        <TeamsportPlayerDetailsDialog
            v-if="playerDetails.show"
            v-model="playerDetails.show"
            :user="playerDetails.user"
            :match="match"
            :teamId="team.id"
            :attendance="attendance"
            :showAtX="playerDetails.x"
            :showAtY="playerDetails.y"
            @updateAttendance="() => { $emit('updateAttendance') }" />

    </v-container>
</template>

<script>
import MultiColumnList from './MultiColumnList.vue';
import TeamsportInvitePlayersDialog from './InvitePlayersDialog.vue';
import TeamsportPlayerDetailsDialog from './PlayerDetailsDialog.vue';
import PlayerFormationWithCaptain from './PlayerFormationWithCaptain.vue';

export default {
    name: 'zoezi-match-team-overview',

    components: {
        MultiColumnList,
        TeamsportInvitePlayersDialog,
        TeamsportPlayerDetailsDialog,
        PlayerFormationWithCaptain
    },

    props: {
        match: {
            type: Object,
            required: true
        },
        members: {
            type: Array,
            required: true
        },
        attendance: {
            type: Array,
            required: true
        },
        team: {
            type: Object,
            required: true
        }
    },

    data: () => ({
        manageFormation: {
            show: false,
            activity: {}
        },
        playerDetails: {
            show: false,
            user: {},
            x: 0,
            y: 0
        },
        editCaptain: {
            show: false,
            user: null
        },
    }),

    computed: {
        questions() {
            return this.match.division.competitionstep.gameclass.questions?.filter(q => q.show_on_match) ?? [];
        },
        showUserDetails() {
            return this.$store.state.user && (this.$api.hasPermission('Competition handling') || this.isTeamLeaderInAnyTeam || this.isReferee);
        },
        leaders() {
            if (!this.match || !this.members.length || !this.team || !this.team.group) {
                return [];
            }

            const teamResponsibles = this.team.group.responsible || [];

            return this.team.group.responsible.map(user_id => {
                const user = this.members.find(x => x.id == user_id);
                return {
                    id: user.id,
                    name: user.name,
                    phone: user.phone,
                    mail: user.mail,
                    leader: teamResponsibles.includes(user.id),
                    user_type: this.team.group.user_types?.[user.id]
                }
            }).sort((a, b) => a.name.localeCompare(b.name));
        },
        players() {
            if (!this.match || !this.members.length) {
                return [];
            }

            const teamResponsibles = this.team.group.responsible || [];

            return this.teamActivity.bookings.map(booking => {
                const user = this.members.find(x => x.id == booking.user_id);

                return {
                    name: user.name,
                    id: user.id,
                    shirt_number: this.getUserShirtNumber(user.id),
                    phone: user.phone,
                    leader: teamResponsibles.includes(user.id),
                    captain: this.match.team_captains[this.team.id] == user.id,
                    mail: user.mail
                }
            }).sort((a, b) => a.name.localeCompare(b.name));
        },
        leaderPresent() {
            return !!this.players.find(p => p.leader);
        },
        requireCaptain() {
            return this.match?.team_captain && this.players.length > 1 && !this.leaderPresent;
        },
        captain() {
            return this.players.find(p => p.captain) || null;
        },
        isReferee() {
            return this.$store.state.user && this.match.referees_id && this.match.referees_id.includes(this.$store.state.user.id);
        },
        teamActivity() {
            if (!this.match.team_activities || !this.team.group) {
                return null;
            }

            return this.match.team_activities.find(x => x.bookable_for_groups.includes(this.team.group.id));
        },
        canManageFormation() {
            const refereeResponsible = this.match.division.competitionstep.gameclass.competition.teamsport.referee_responsible_for_formations

            return this.isTeamLeader || (refereeResponsible && this.isReferee);
        },
        canEditCaptain() {
            return this.isTeamLeader || this.$api.hasPermission('Competition handling');
        },
        isTeamLeader() {
            return this.team.group?.responsible?.includes(this.$store.state.user?.id);
        },
        isTeamLeaderInAnyTeam() {
            return this.match.teams.some(t => t.group?.responsible?.includes(this.$store.state.user?.id))
        }
    },

    methods: {
        saveCaptain(){
            const url = this.$api.hasPermission('Competition handling') ? '/api/teamsport/match/team/captain/set' : '/api/memberapi/teamsport/match/team/captain/set';
            this.$api.post(url, {
                matchId: this.match.id,
                teamId: this.team.id,
                captainId: this.editCaptain.user,
            });
        },
        enableEditCaptain() {
            if (!this.canEditCaptain) {
                return;
            }
            this.editCaptain.user = this.captain;
            this.editCaptain.show = true;
        },
        getUserShirtNumber(userId) {
            let match_number = this.match.shirt_numbers?.find(user => user.user == userId && user.team == this.team.id);
            let group_number = this.team?.group?.shirt_numbers?.find(user => user.user_id == userId);

            return match_number?.shirt || group_number?.shirt || null;
        },
        showPlayerDetails(user, $event) {
            if (this.showUserDetails && this.canManageFormation) {
                this.playerDetails.user = user;
                this.playerDetails.teamId = this.team.id;
                this.playerDetails.show = true;

                if (this.$vuetify.breakpoint.mobile) {
                    this.playerDetails.x = (window.innerWidth - 275) / 2;
                    this.playerDetails.y = $event.clientY + 40;
                } else {
                    // If has space to the left, put it there
                    if ($event.clientX > 285) {
                        this.playerDetails.x = $event.clientX - 285;
                        this.playerDetails.y = $event.clientY;
                    } else {
                        this.playerDetails.x = $event.clientX + 100;
                        this.playerDetails.y = $event.clientY;
                    }
                }
                this.playerDetails.x -= 15
                this.playerDetails.y -= 15
            }
        },
        showManageFormationDialog(team) {
            this.manageFormation.show = true;
            this.manageFormation.teamId = team.id;
            this.manageFormation.group = team.group;

            const teamIndex = this.match.teams.findIndex(x => x.id == team.id);
            this.manageFormation.activity = this.match.team_activities[teamIndex];
        },
        isPresent(userId) {
            return this.attendance?.find(x => x.user.id == userId)?.present;
        }
    }
}
</script>
